import React, { useEffect, useState, useRef } from 'react';
import { CSVLink } from 'react-csv';
import DataTable from 'react-data-table-component';
import { POST_AEPS_MONTHLY_COMMISSION } from '../../Utils/appConstants';
import { toDayDate, queryDate } from '../../Utils/function';
import { postRequest } from '../../Utils/AppApi';
import { INRFormat } from '../../Utils/Common';
import { useHistory } from "react-router-dom";
import { DatePicker, Tooltip, Input, Button } from 'antd';
import { toast } from 'react-toastify';
import moment from 'moment';

function Index(props) {
    const history = useHistory();
    const csvLink = useRef();
    const dateFormat = 'DD-MM-YYYY';

    const [endDate, setEndDate] = useState(toDayDate());
    const [startDate, setStartDate] = useState(toDayDate());
    const [searchTerm, setSearchTerm] = useState('');
    const [getData, setData] = useState([]);
    const [getCsvData, setCsvData] = useState([]);
    const [isLoading, setLoading] = useState(false);
    const [processingRow, setProcessingRow] = useState(null);

    const buildPayload = (overrides = {}) => {
        const payload = {
            startDate: startDate ? queryDate(startDate) : '',
            endDate: endDate ? queryDate(endDate) : '',
            search: searchTerm.trim() || undefined,
            ...overrides
        };
        Object.keys(payload).forEach(key => payload[key] === undefined && delete payload[key]);
        return payload;
    };

    const getDataReports = async (search = searchTerm) => {
        setLoading(true);
        try {
            const payload = buildPayload({ isProcess: false, search });
            const response = await postRequest(POST_AEPS_MONTHLY_COMMISSION, payload, history);
            if (response.success) {
                setData(response.data || []);
            } else {
                toast.error(response.message || 'Failed to fetch data');
                setData([]);
            }
        } catch (error) {
            console.error('Fetch error:', error);
            toast.error('Network error');
            setData([]);
        } finally {
            setLoading(false);
        }
    };

    const handleProcessRow = async (userId) => {
        setProcessingRow(userId);
        try {
            const payload = buildPayload({ isProcess: true, userId });
            const response = await postRequest(POST_AEPS_MONTHLY_COMMISSION, payload, history);
            if (response.success) {
                toast.success('Commission processed for this user');
                // Optimistically update the row
                setData(prevData =>
                    prevData.map(row =>
                        row._id[0] === userId
                            ? { ...row, isProcessed: true, processedAt: new Date().toISOString(), totalAmount: 0 }
                            : row
                    )
                );
            } else {
                toast.error(response.message || 'Processing failed');
            }
        } catch (error) {
            console.error('Process row error:', error);
            toast.error('Network error');
        } finally {
            setProcessingRow(null);
        }
    };

    const handleProcess = async () => {
        setLoading(true);
        try {
            const payload = buildPayload({ isProcess: true });
            const response = await postRequest(POST_AEPS_MONTHLY_COMMISSION, payload, history);
            if (response.success) {
                toast.success('Commission transferred to wallet successfully');
                await getDataReports(searchTerm);
            } else {
                toast.error(response.message || 'Bulk processing failed');
            }
        } catch (error) {
            console.error('Bulk process error:', error);
            toast.error('Network error');
        } finally {
            setLoading(false);
        }
    };

    const getCSVDataList = async () => {
        try {
            const payload = buildPayload({ isProcess: false });
            const response = await postRequest(POST_AEPS_MONTHLY_COMMISSION, payload, history);
            if (response.success) {
                const csvdataRecord = response.data.map(e => ({
                    'User ID': e._id[1],
                    'Name': e._id[2],
                    'Total Amount': e.totalAmount,
                    'Released On': e.processedAt ? moment(e.processedAt).format('DD-MM-YYYY HH:mm') : 'Not processed'
                }));
                setCsvData(csvdataRecord);
                setTimeout(() => csvLink.current.link.click(), 500);
            } else {
                toast.error('Failed to export data');
            }
        } catch (error) {
            console.error('Export error:', error);
            toast.error('Network error');
        }
    };

    const onChangeStartDate = (date) => setStartDate(date);
    const onChangeEndDate = (date) => setEndDate(date);

    const handleClear = () => {
        setStartDate('');
        setEndDate('');
        setSearchTerm('');
        getDataReports('');
    };

    const handleSearch = () => getDataReports(searchTerm);

    useEffect(() => {
        getDataReports();
        // eslint-disable-next-line
    }, []);

    const columns = [
        {
            name: 'User ID',
            selector: row => <Tooltip placement="bottom" title={row._id[1]}>{row._id[1]}</Tooltip>,
        },
        {
            name: 'Name',
            selector: row => <Tooltip placement="bottom" title={row._id[2]}>{row._id[2]}</Tooltip>,
        },
        {
            name: 'Total Amt',
            selector: row => INRFormat(row.totalAmount),
        },
        {
            name: 'Released On',
            selector: row => row.processedAt ? moment(row.processedAt).format('DD-MM-YYYY HH:mm') : '–',
        },
        {
            name: 'Action',
            cell: row => (
                <Button
                    type="primary"
                    size="small"
                    loading={processingRow === row._id[0]}
                    disabled={isLoading || row.isProcessed || processingRow === row._id[0]}
                    onClick={() => handleProcessRow(row._id[0])}
                >
                    {row.isProcessed ? 'Processed' : 'Process'}
                </Button>
            ),
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
        }
    ];

    return (
        <div>
            <div className="content-wrapper pt-2">
                <div className="content-header my-0 py-0">
                    <div className="container-fluid">
                        <div className="row mb-0 align-items-center">
                            <div className="col-sm-6">
                                <h1 className='page-title text-blue font-merriweather'>Monthly <span className='text-red'>DMT Commission</span></h1>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="content">
                    <div className="container-fluid pt-2 pb-5">
                        <div className='card col-md-12'>
                            <div className='card-body'>
                                <div className="row">
                                    <div className='col-md-12 date-search mb-0'>
                                        <div className='row align-items-end'>
                                            <div className='col-md-3 col-lg-3 d-flex'>
                                                <div className="form-group mr-3">
                                                    <label className='date-search-label mb-0 text-blue'>From</label>
                                                    <DatePicker className="form-control form-control-border" value={startDate} format={dateFormat} onChange={onChangeStartDate} />
                                                </div>
                                                <div className="form-group">
                                                    <label className='date-search-label mb-0 text-red'>To</label>
                                                    <DatePicker className="form-control form-control-border" value={endDate} format={dateFormat} onChange={onChangeEndDate} />
                                                </div>
                                            </div>

                                            <div className='col-md-2 col-lg-2'>
                                                <div className="form-group">
                                                    <label className='date-search-label mb-0 text-dark'>Search User</label>
                                                    <Input
                                                        placeholder="Username or Name"
                                                        value={searchTerm}
                                                        onChange={(e) => setSearchTerm(e.target.value)}
                                                        onPressEnter={handleSearch}
                                                        allowClear
                                                    />
                                                </div>
                                            </div>

                                            <div className='col-md-1 col-lg-1'>
                                                <button type="submit" className="btn btn-primary rounded-pill px-3" onClick={handleSearch}>Search</button>
                                            </div>
                                            <div className='col-md-1 col-lg-1'>
                                                <button type="button" className="btn btn-default rounded-pill px-4" onClick={handleClear}>Clear</button>
                                            </div>
                                            <div className='col-md-1 col-lg-1'>
                                                <button type="submit" className="btn btn-info rounded-pill px-3" disabled={isLoading || processingRow} onClick={handleProcess}>Process All</button>
                                            </div>
                                            <div className='col-md-2'>
                                                <div className='float-sm-right export-button'>
                                                    <button type="button" className="btn btn-primary mr-3" onClick={getCSVDataList}><i className="fa fa-file-excel"></i> Export</button>
                                                    <CSVLink
                                                        data={getCsvData}
                                                        filename='monthly-dmt-commission'
                                                        className='hidden'
                                                        ref={csvLink}
                                                        target='_blank'
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="container-fluid pt-2 pb-5">
                                    <DataTable
                                        title={""}
                                        columns={columns}
                                        data={getData}
                                        highlightOnHover
                                        progressPending={isLoading}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Index;